import { useState, useEffect, useRef } from 'react'
import { GlobalGuestArtistsMap } from '@/components/guestartists/GlobalGuestArtistsMap'
import { AdminAuthGuard } from '@/components/admin/AdminAuthGuard'

interface EnrichedArtist {
  username: string
  name: string
  followers: number
  bio: string
  profile_image_url: string
  home_city: string
  home_country: string | null
  home_lat?: number
  home_lon?: number
  guestSpots: Array<{
    city: string
    country: string
    start_date: string | null
    end_date: string | null
    lat?: number
    lon?: number
  }>
  network_score: number
}

type ViewMode = 'artists' | 'guests' | 'both'
type RegionFilter = 'global' | 'europe'

// Bounding box covering continental Europe + UK/Ireland/Iceland
const EUROPE_BOUNDS = { latMin: 34, latMax: 72, lonMin: -25, lonMax: 45 }

function isInEurope(lat: number, lon: number): boolean {
  return (
    lat >= EUROPE_BOUNDS.latMin &&
    lat <= EUROPE_BOUNDS.latMax &&
    lon >= EUROPE_BOUNDS.lonMin &&
    lon <= EUROPE_BOUNDS.lonMax
  )
}

export default function GlobalGuestArtistsPage() {
  const [allArtists, setAllArtists] = useState<EnrichedArtist[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<ViewMode>('both')
  const [regionFilter, setRegionFilter] = useState<RegionFilter>('global')
  
  const headerRef = useRef<HTMLDivElement>(null)
  const [mapHeight, setMapHeight] = useState<number>(0)

  useEffect(() => {
    const updateMapHeight = () => {
      if (!headerRef.current) return
      const navOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--bottom-nav-offset') || '0')
      const availableHeight = window.innerHeight - headerRef.current.offsetHeight - navOffset
      console.log('[GlobalGuestArtistsPage] mapHeight calc — window:', window.innerHeight, '| header:', headerRef.current.offsetHeight, '| nav:', navOffset, '| result:', availableHeight)
      setMapHeight(availableHeight)
    }

    updateMapHeight()
    window.addEventListener('resize', updateMapHeight)
    return () => window.removeEventListener('resize', updateMapHeight)
  }, [isLoading])

  const artists = regionFilter === 'europe'
    ? allArtists
        .filter(a => a.home_lat && a.home_lon && isInEurope(a.home_lat, a.home_lon))
        .map(a => ({
          ...a,
          guestSpots: a.guestSpots.filter(s => s.lat && s.lon && isInEurope(s.lat, s.lon)),
        }))
    : allArtists

  const artistsWithGuestSpots = artists.filter(a => a.guestSpots && a.guestSpots.length > 0)
  const totalGuestSpots = artists.reduce((sum, a) => sum + (a.guestSpots?.length || 0), 0)

  useEffect(() => {
    const loadArtistsData = async () => {
      try {
        const response = await fetch('/assets/mini-artist-db.json')
        if (!response.ok) {
          throw new Error('Failed to load artists data')
        }
        const data = await response.json()
        
        // Filter artists that have home coordinates
        const artistsWithValidSpots = data.filter((artist: EnrichedArtist) => {
          if (!artist.home_lat || !artist.home_lon) return false
          return true
        })
        
        // Filter guest spots to only show valid ones (with dates, coordinates, and different from home city)
        const artistsWithFilteredSpots = artistsWithValidSpots.map((artist: EnrichedArtist) => ({
          ...artist,
          guestSpots: artist.guestSpots ? artist.guestSpots.filter((spot: EnrichedArtist['guestSpots'][number]) => {
            if (!spot.start_date || !spot.end_date || !spot.lat || !spot.lon) return false
            if (spot.city === artist.home_city) return false
            return true
          }) : []
        }))
        
        console.log('[GlobalGuestArtistsPage] Loaded artists:', artistsWithFilteredSpots.length)
        console.log('[GlobalGuestArtistsPage] Artists with valid guest spots:', artistsWithFilteredSpots.filter((a: EnrichedArtist) => a.guestSpots && a.guestSpots.length > 0).length)
        setAllArtists(artistsWithFilteredSpots)
      } catch (err) {
        console.error('[GlobalGuestArtistsPage] Error loading artists:', err)
        setError('Failed to load artist data')
      } finally {
        setIsLoading(false)
      }
    }

    loadArtistsData()
  }, [])

  return (
    <AdminAuthGuard>
      <>
        <div className="flex flex-col bg-background h-screen-mobile-minus-nav">
          {/* Header */}
          <div ref={headerRef} className="flex-shrink-0 sticky top-0 z-50 bg-background border-b border-border">
            <div className="phone-container-padded py-4">
              <h1 className="text-2xl font-bold mb-3">
                {regionFilter === 'europe' ? 'European Guest Artists' : 'Global Guest Artists'}
              </h1>
              
              {/* View Mode Selector */}
              <div className="flex gap-1 bg-muted p-1 rounded-lg mb-2">
                <button
                  onClick={() => setViewMode('artists')}
                  className={`flex-1 px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                    viewMode === 'artists' 
                      ? 'bg-background text-foreground shadow-sm' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Artists
                </button>
                <button
                  onClick={() => setViewMode('guests')}
                  className={`flex-1 px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                    viewMode === 'guests' 
                      ? 'bg-background text-foreground shadow-sm' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Guests
                </button>
                <button
                  onClick={() => setViewMode('both')}
                  className={`flex-1 px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                    viewMode === 'both' 
                      ? 'bg-background text-foreground shadow-sm' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Both
                </button>
                <button
                  onClick={() => setRegionFilter(r => r === 'europe' ? 'global' : 'europe')}
                  className={`flex-1 px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                    regionFilter === 'europe'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  🇪🇺 EU
                </button>
              </div>
              
              {isLoading ? (
                <p className="text-sm text-muted-foreground mt-1">Loading...</p>
              ) : (
                <div className="mt-2 space-y-1">
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                    <span className="text-muted-foreground">
                      <span className="font-semibold text-foreground">{artists.length}</span> artists {regionFilter === 'europe' ? 'in Europe' : 'worldwide'}
                    </span>
                    <span className="text-muted-foreground">
                      <span className="font-semibold text-foreground">{artistsWithGuestSpots.length}</span> with guest spots
                    </span>
                    <span className="text-muted-foreground">
                      <span className="font-semibold text-foreground">{totalGuestSpots}</span> total guest spots
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Map Section */}
          <div className="w-full relative" style={{ height: mapHeight || undefined, flex: mapHeight ? 'none' : '1 1 0', minHeight: mapHeight ? undefined : 0 }}>
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="mb-4 flex justify-center">
                    <div className="w-8 h-8 border-3 border-muted-foreground/30 border-t-foreground rounded-full animate-spin" />
                  </div>
                  <p className="text-muted-foreground">Loading artist data...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-destructive">
                  <p>{error}</p>
                </div>
              </div>
            ) : (
              (() => {
                console.log('[GlobalGuestArtistsPage] rendering map — artists:', artists.length, '| viewMode:', viewMode, '| mapHeight:', mapHeight)
                return <GlobalGuestArtistsMap artists={artists} viewMode={viewMode} />
              })()
            )}
          </div>
        </div>
      </>
    </AdminAuthGuard>
  )
}

